import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  ModalProps,
  Theme,
  Typography,
  makeStyles,
  useTheme,
} from '@forgeglobal/design-framework'
import { BidAskDetailRow } from 'components/order-book/BidAskDetailRow'
import { ColumnSortProps, ExpansionTable } from 'isolated-design-library'
import {
  ListingStructureType,
  OrderBookBidAskDetailQuery,
  SortDirection,
  TradeIntentType,
  useOrderBookBidAskDetailQuery,
} from 'graphql/bindings/AdminBindings'
import { titleCase } from 'util/StringUtils'
import React, { useState } from 'react'

export interface BidAskModalProps {
  companyId: number
  type: 'bid' | 'ask'
  status: 'actionable' | 'active'
  structures: string[]
  count: number
}

export const ColumnDefs = [
  { name: 'account_name', text: 'Account', width: 2.5, sortable: true },
  { name: 'owner', text: 'Owner', width: 1.5 },
  { name: 'share_price', text: 'Gross Share Price', width: 1.2, sortable: true },
  { name: 'dollar_amount', text: 'Target Size', width: 1.2, sortable: true },
  { name: 'status', text: 'Status', width: 1, sortable: true },
  { name: 'structure', text: 'Structure', width: 1.5 },
  { name: 'date', text: 'Last Updated', width: 1.5, sortable: true },
]

const useStyles = makeStyles((theme: Theme) => ({
  modalTitleContainer: {
    display: 'inline-flex',
  },
  avatar: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  modalShaded: {
    backgroundColor: theme.palette.background.default,
  },
  headingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export const BidAskDetailModal: React.FC<BidAskModalProps & ModalProps> = ({
  companyId,
  type,
  status,
  structures,
  title,
  hideModal,
  count,
}) => {
  const theme = useTheme()
  const oppType = type === 'bid' ? TradeIntentType.Bid : TradeIntentType.Ask
  const oppTypeTitle = type === 'bid' ? 'Bids' : 'Asks'
  const displayIfNone = <Typography variant="caption">{`No qualified ${type}s were found`}</Typography>

  const defaultSort = {
    field: 'share_price',
    direction: type === 'bid' ? SortDirection.Descending : SortDirection.Ascending,
  }

  const [sortState, setSortState] = useState<ColumnSortProps>(defaultSort)

  const sortDirection = (field: string, direction: SortDirection) => {
    // The graphql type for dates is actual dates, but these are displayed in the UI
    // in relative terms (e.g. 3 months ago). Since the greatest (latest) date gives
    // the least time ago, reverse the sort order on requests to match what the user
    // expects from the UI.
    if (field === 'date') {
      return direction === SortDirection.Descending ? SortDirection.Ascending : SortDirection.Descending
    }

    // for remaining fields, request sort order as specified in the UI
    return direction
  }

  const onClickSort = (columnName: string) => {
    const column = ColumnDefs.find(col => col.name === columnName)
    if (!column || !column.sortable) return

    if (!sortState || columnName !== sortState.field) {
      setSortState({ field: columnName, direction: SortDirection.Ascending })
    } else if (columnName === defaultSort.field) {
      setSortState({
        field: columnName,
        direction: sortState.direction === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending,
      })
    } else if (sortState && sortState.direction === SortDirection.Ascending) {
      setSortState({ field: columnName, direction: SortDirection.Descending })
    } else {
      setSortState(defaultSort)
    }
  }

  const queryVars = {
    limit: 20,
    offset: 0,
    companyId: companyId,
    filter: {
      companyId: companyId,
      type: oppType,
      status: status,
      structures: structures.includes('all') ? undefined : (structures as ListingStructureType[]),
      expired: false,
      sortField: sortState ? sortState.field : null,
      sortDirection: sortState ? sortDirection(sortState.field, sortState.direction) : null,
    },
  }
  const { data, loading, fetchMore } = useOrderBookBidAskDetailQuery({ variables: queryVars })
  const company = data?.company
  const rowData =
    data && !loading ? data.opportunities.data : Array<OrderBookBidAskDetailQuery['opportunities']['data'][0]>()

  const loadMore = (fromIndex: number, toIndex: number) => {
    if (loading || toIndex < rowData.length) {
      return Promise.resolve()
    }

    return fetchMore({
      variables: Object.assign({}, queryVars, {
        offset: rowData.length,
      }),
      updateQuery: (prev: OrderBookBidAskDetailQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return Object.assign({}, prev, {
          opportunities: prev.opportunities.data.slice(0, rowData.length).concat(fetchMoreResult.opportunities.data),
        })
      },
    }).then(() => Promise.resolve())
  }

  const classes = useStyles()

  const rowHeight = theme.spacing(8)
  const enoughForSixRows = 6 * rowHeight

  return (
    <Dialog open maxWidth="lg" fullWidth PaperProps={{ classes: { root: 'modal-can-overflow' } }}>
      <DialogTitle onClose={hideModal}>
        <div className={classes.modalTitleContainer}>
          {title}
          <span className={classes.avatar}>
            <Avatar src={company?.logo_url ?? ''} />
          </span>
          {company?.name ?? ''}
        </div>
      </DialogTitle>
      <Divider />
      <DialogContent className={classes.modalShaded}>
        <Box paddingX={3} paddingBottom={2} className={classes.headingContainer}>
          <Typography color="secondary" variant="h6" style={{ textTransform: 'none' }}>
            {`${titleCase(status)} ${oppTypeTitle}`}
          </Typography>
          <div style={{ flexGrow: 1 }} />
          <Typography color="textSecondary" variant="subtitle2"></Typography>
        </Box>
        <Divider />
        <ExpansionTable
          columnDefs={ColumnDefs}
          sort={sortState}
          onClickSort={onClickSort}
          tableHeight={enoughForSixRows}
          rowHeights={{ collapsed: rowHeight }}
          data={rowData}
          loadMore={loadMore}
          component={BidAskDetailRow}
          displayIfNone={displayIfNone}
          rowCount={count}
        />
      </DialogContent>
    </Dialog>
  )
}
